import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import _truncate from 'lodash/truncate';
import _difference from 'lodash/difference';
import _isEqual from 'lodash/isEqual';
import _keyBy from 'lodash/keyBy';
import { canadianProvincesWithRegion, americanStatesWithRegion } from 'constants/regions';
import { InputDropdown, MultiLevelDropdown, InputAdornment, Icon, Chip } from 'bonfire-kindling';
import useOverloadedInputDropdown from '../hooks/useOverloadedInputDropdown';

const treeData = [
  {
    value: 'CAN',
    label: 'Canada',
    children: canadianProvincesWithRegion.map(({ value, label }) => ({
      value,
      label,
    })),
  },
  {
    value: 'USA',
    label: 'United States',
    children: americanStatesWithRegion.map(({ value, label }) => ({
      value,
      label,
    })),
  },
];
const getFilteredTree = available => {
  if (!available || !available.length) {
    return treeData;
  }
  const availableIndexed = _keyBy(available);
  const newTree = treeData.reduce((countries, country) => {
    const availableChildren = country.children.filter(({ value }) => availableIndexed[value]);
    if (availableChildren.length) {
      countries.push({ ...country, children: availableChildren });
    }
    return countries;
  }, []);
  return newTree;
};

const getCheckedLabels = checked =>
  treeData.reduce((values, parent) => {
    const children = parent.children.map(child => child.value);
    const allChildrenChecked = _difference(children, checked).length === 0;
    if (allChildrenChecked) {
      values.push(parent.label);
    } else {
      values.push(
        ...parent.children.filter(child => checked.includes(child.value)).map(child => child.label)
      );
    }

    return values;
  }, []);

const formatInputValue = checkedLabels =>
  _truncate(checkedLabels.join(', '), { length: 35, separator: ', ' });

const FilterByLocation = ({ onChange, values, availableValues, label }) => {
  const { t } = useTranslation();
  const availableTree = useMemo(() => getFilteredTree(availableValues), [availableValues]);
  const checkedLabels = getCheckedLabels(values);
  const inputValueFormatted = formatInputValue(checkedLabels);
  const { overloadedCount } = useOverloadedInputDropdown(inputValueFormatted, checkedLabels);

  return (
    <InputDropdown
      aria-label={t('opportunities.filters.location_filter')}
      label={label}
      value={inputValueFormatted}
      TextFieldProps={{
        placeholder: t('opportunities.filters.location_placeholder'),
        sx: { width: '15rem' },
        InputProps: {
          endAdornment: (
            <InputAdornment position="end">
              {overloadedCount > 0 && (
                <Chip size="small" label={`+${overloadedCount}`} color="primary" />
              )}
              <Icon iconName="caret-down" mr={1} size="small" />
            </InputAdornment>
          ),
          inputProps: {
            'data-cy': 'locationFilter',
          },
        },
      }}
    >
      <MultiLevelDropdown
        initialChecked={availableValues}
        checked={values}
        nodes={availableTree}
        noResultsLabel={t('opportunities.filters.no_locations')}
        disableReset
        onCheck={checked => {
          if (!_isEqual(checked, values)) {
            onChange(checked);
          }
        }}
      />
    </InputDropdown>
  );
};

export default FilterByLocation;
